import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Grid,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  LinearProgress,
  Divider,
  useTheme,
} from '@mui/material';
import {
  TrendingUp,
  TrendingDown,
  Receipt,
  Assessment,
  MonetizationOn,
  ShoppingCart,
  Calculate,
} from '@mui/icons-material';
import { MonthlyReportDTO } from '../../types/expense.types';

interface AccountingDashboardProps {
  report: MonthlyReportDTO;
  loading?: boolean;
}

/**
 * Dashboard comptable professionnel pour afficher toutes les statistiques
 */
export const AccountingDashboard: React.FC<AccountingDashboardProps> = ({
  report,
  loading = false,
}) => {
  const theme = useTheme();

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR',
    }).format(amount);
  };

  const formatPercentage = (value: number) => {
    return `${value.toFixed(2)} %`;
  };

  const getMonthName = (month?: number) => {
    if (!month) return '';
    const monthNames = [
      'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
      'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'
    ];
    return monthNames[month - 1];
  };

  const getPeriodLabel = () => {
    if (report.periodType === 'MONTH' && report.month) {
      return `${getMonthName(report.month)} ${report.year}`;
    }
    if (report.periodType === 'QUARTER' && report.quarter) {
      return `Trimestre ${report.quarter} ${report.year}`;
    }
    return `Année ${report.year}`;
  };

  if (loading) {
    return (
      <Box sx={{ width: '100%', p: 3 }}>
        <LinearProgress />
      </Box>
    );
  }

  /**
   * Carte KPI avec icône et valeur
   */
  const KPICard: React.FC<{
    title: string;
    value: string;
    subtitle?: string;
    icon: React.ReactNode;
    color: string;
    trend?: 'up' | 'down' | 'neutral';
  }> = ({ title, value, subtitle, icon, color, trend }) => (
    <Card
      elevation={2}
      sx={{
        height: '100%',
        borderLeft: `4px solid ${color}`,
        transition: 'transform 0.2s, box-shadow 0.2s',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: theme.shadows[6],
        },
      }}
    >
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
            {title}
          </Typography>
          <Box
            sx={{
              backgroundColor: `${color}20`,
              borderRadius: 1,
              p: 0.5,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {icon}
          </Box>
        </Box>
        <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
          {value}
        </Typography>
        {subtitle && (
          <Typography variant="caption" color="text.secondary">
            {subtitle}
          </Typography>
        )}
        {trend && (
          <Box sx={{ display: 'flex', alignItems: 'center', mt: 1, gap: 0.5 }}>
            {trend === 'up' && <TrendingUp fontSize="small" sx={{ color: '#10B981' }} />}
            {trend === 'down' && <TrendingDown fontSize="small" sx={{ color: '#EF4444' }} />}
          </Box>
        )}
      </CardContent>
    </Card>
  );

  return (
    <Box>
      {/* En-tête de période */}
      <Paper
        elevation={2}
        sx={{
          p: 3,
          mb: 3,
          borderLeft: `4px solid ${theme.palette.primary.main}`,
          bgcolor: 'background.paper',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
          <Box
            sx={{
              backgroundColor: `${theme.palette.primary.main}20`,
              borderRadius: 1,
              p: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Assessment sx={{ color: theme.palette.primary.main, fontSize: 28 }} />
          </Box>
          <Typography variant="h5" sx={{ fontWeight: 700, color: 'text.primary' }}>
            Rapport Comptable
          </Typography>
        </Box>
        <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary', mb: 0.5 }}>
          {getPeriodLabel()}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Du {new Date(report.startDate).toLocaleDateString('fr-FR')} au{' '}
          {new Date(report.endDate).toLocaleDateString('fr-FR')}
        </Typography>
      </Paper>

      {/* KPIs Principaux */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        {/* Chiffre d'Affaires */}
        <Grid item xs={12} sm={6} md={3}>
          <KPICard
            title="Chiffre d'Affaires HT"
            value={formatCurrency(report.chiffreAffairesHT)}
            subtitle={`${report.nombreFacturesPayees} facture(s) payée(s)`}
            icon={<MonetizationOn sx={{ fontSize: 32 }} />}
            color="#10B981"
            trend="up"
          />
        </Grid>

        {/* Dépenses */}
        <Grid item xs={12} sm={6} md={3}>
          <KPICard
            title="Dépenses HT"
            value={formatCurrency(report.totalDepensesHT)}
            subtitle={`${report.nombreDepensesValidees} dépense(s) validée(s)`}
            icon={<ShoppingCart sx={{ fontSize: 32 }} />}
            color="#EF4444"
          />
        </Grid>

        {/* Marge Brute */}
        <Grid item xs={12} sm={6} md={3}>
          <KPICard
            title="Marge Brute"
            value={formatCurrency(report.margeBrute)}
            subtitle={`Taux de marge: ${formatPercentage(report.tauxMarge)}`}
            icon={<TrendingUp sx={{ fontSize: 32 }} />}
            color="#F59E0B"
          />
        </Grid>

        {/* Résultat Net */}
        <Grid item xs={12} sm={6} md={3}>
          <KPICard
            title="Résultat Net"
            value={formatCurrency(report.resultatNet)}
            subtitle="Après TVA"
            icon={<Assessment sx={{ fontSize: 32 }} />}
            color={report.resultatNet >= 0 ? '#10B981' : '#EF4444'}
            trend={report.resultatNet >= 0 ? 'up' : 'down'}
          />
        </Grid>
      </Grid>

      {/* Détails TVA */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} lg={8}>
          <Card elevation={2}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <Calculate sx={{ color: '#8B5CF6' }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Détail de la TVA
                </Typography>
              </Box>

              <Divider sx={{ mb: 2 }} />

              {/* TVA Collectée */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  TVA Collectée (sur vos ventes)
                </Typography>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Taux</TableCell>
                        <TableCell align="right">Base HT</TableCell>
                        <TableCell align="right">TVA</TableCell>
                        <TableCell align="right">TTC</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {report.tvaReport.tvaCollectee.map((tva, index) => (
                        <TableRow key={index}>
                          <TableCell>
                            <Chip label={`${tva.tauxTVA}%`} size="small" color="primary" />
                          </TableCell>
                          <TableCell align="right">{formatCurrency(tva.baseHT)}</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600 }}>
                            {formatCurrency(tva.montantTVA)}
                          </TableCell>
                          <TableCell align="right">{formatCurrency(tva.montantTTC)}</TableCell>
                        </TableRow>
                      ))}
                      <TableRow>
                        <TableCell colSpan={2} sx={{ fontWeight: 700 }}>
                          Total TVA Collectée
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700, color: '#10B981' }}>
                          {formatCurrency(report.tvaReport.totalTVACollectee)}
                        </TableCell>
                        <TableCell />
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>

              {/* TVA Déductible */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  TVA Déductible (sur vos achats)
                </Typography>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Taux</TableCell>
                        <TableCell align="right">Base HT</TableCell>
                        <TableCell align="right">TVA</TableCell>
                        <TableCell align="right">TTC</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {report.tvaReport.tvaDeductible.map((tva, index) => (
                        <TableRow key={index}>
                          <TableCell>
                            <Chip label={`${tva.tauxTVA}%`} size="small" color="secondary" />
                          </TableCell>
                          <TableCell align="right">{formatCurrency(tva.baseHT)}</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600 }}>
                            {formatCurrency(tva.montantTVA)}
                          </TableCell>
                          <TableCell align="right">{formatCurrency(tva.montantTTC)}</TableCell>
                        </TableRow>
                      ))}
                      <TableRow>
                        <TableCell colSpan={2} sx={{ fontWeight: 700 }}>
                          Total TVA Déductible
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700, color: '#EF4444' }}>
                          {formatCurrency(report.tvaReport.totalTVADeductible)}
                        </TableCell>
                        <TableCell />
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>

              {/* TVA à reverser */}
              <Paper
                sx={{
                  p: 2,
                  bgcolor: report.tvaReport.creditDeTVA ? '#DBEAFE' : '#FEE2E2',
                  border: `2px solid ${report.tvaReport.creditDeTVA ? '#3B82F6' : '#EF4444'}`,
                }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    {report.tvaReport.creditDeTVA ? '💰 Crédit de TVA' : '📤 TVA à reverser'}
                  </Typography>
                  <Typography
                    variant="h5"
                    sx={{
                      fontWeight: 900,
                      color: report.tvaReport.creditDeTVA ? '#3B82F6' : '#EF4444',
                    }}
                  >
                    {formatCurrency(Math.abs(report.tvaReport.tvaAReverser))}
                  </Typography>
                </Box>
                <Typography variant="caption" color="text.secondary">
                  {report.tvaReport.creditDeTVA
                    ? 'Vous avez un crédit de TVA à récupérer'
                    : 'Montant à verser à l\'administration fiscale'}
                </Typography>
              </Paper>
            </CardContent>
          </Card>
        </Grid>

        {/* Indicateurs de performance */}
        <Grid item xs={12} lg={4}>
          <Card elevation={2} sx={{ height: '100%' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <Receipt sx={{ color: '#F59E0B' }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Indicateurs
                </Typography>
              </Box>

              <Divider sx={{ mb: 2 }} />

              {/* Montant moyen facture */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Montant moyen par facture
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 700, color: '#10B981' }}>
                  {formatCurrency(report.montantMoyenFacture)}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Sur {report.nombreFacturesPayees} facture(s)
                </Typography>
              </Box>

              {/* Montant moyen dépense */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Montant moyen par dépense
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 700, color: '#EF4444' }}>
                  {formatCurrency(report.montantMoyenDepense)}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Sur {report.nombreDepensesValidees} dépense(s)
                </Typography>
              </Box>

              {/* CA TTC */}
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Chiffre d'Affaires TTC
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {formatCurrency(report.chiffreAffairesTTC)}
                </Typography>
              </Box>

              {/* Dépenses TTC */}
              <Box>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Total Dépenses TTC
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {formatCurrency(report.totalDepensesTTC)}
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Barre de progression de la marge */}
      <Card elevation={2}>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
            📈 Taux de Marge
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
            <Box sx={{ flex: 1 }}>
              <LinearProgress
                variant="determinate"
                value={Math.min(report.tauxMarge, 100)}
                sx={{
                  height: 12,
                  borderRadius: 6,
                  bgcolor: '#E5E7EB',
                  '& .MuiLinearProgress-bar': {
                    bgcolor:
                      report.tauxMarge >= 50
                        ? '#10B981'
                        : report.tauxMarge >= 30
                        ? '#F59E0B'
                        : '#EF4444',
                    borderRadius: 6,
                  },
                }}
              />
            </Box>
            <Typography variant="h6" sx={{ fontWeight: 700, minWidth: 80, textAlign: 'right' }}>
              {formatPercentage(report.tauxMarge)}
            </Typography>
          </Box>
          <Typography variant="caption" color="text.secondary">
            Un bon taux de marge pour un artisan se situe généralement entre 30% et 50%
          </Typography>
        </CardContent>
      </Card>
    </Box>
  );
};

export default AccountingDashboard;
